import { PlusOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Card, Col, Row, Space, Typography } from 'antd'
import React from 'react'

import './initiativeKanbanBoard.scss'

const { Text } = Typography

/**
 * 카테고리별 태그 색상 매핑
 */
const CATEGORY_TAG_COLORS = {
  개선: '#117FFA',
  신규: '#007757',
  '정책/기준': '#DE9A21',
  '사고 대응': '#DE5D21',
  기타: '#7D51FE',
}

/**
 * 우선순위별 왼쪽 스트립 색상 매핑
 */
const PRIORITY_COLORS = {
  FIRST: '#FF7853',
  SECOND: '#FFCA00',
  THIRD: '#1FC297',
}

const mapPriorityToFrontendColor = (priority) => {
  switch (priority) {
    case 'FIRST':
      return '#FF7853'
    case 'SECOND':
      return '#FFCA00'
    case 'THIRD':
      return '#1FC297'
    default:
      return ''
  }
}

/**
 * Initiative 카드 컴포넌트
 * @param {Object} props
 * @param {Object} props.task - 태스크 데이터
 * @param {string} props.category - 카테고리명 (개선, 신규, 정책/기준, 사고 대응, 기타)
 * @param {Function} props.onClick - 클릭 핸들러
 */
const InitiativeCard = ({ initiative, category, onClick }) => {
  const priorityColor =
    PRIORITY_COLORS[initiative.priority] || PRIORITY_COLORS.FIRST
  const tagColor = CATEGORY_TAG_COLORS[category] || '#117FFA'

  return (
    <div
      className="initiative-card-wrapper"
      onClick={() => onClick?.(initiative)}
    >
      <Card
        className="initiative-card"
        bordered={false}
        bodyStyle={{ padding: 0, height: '100%' }}
      >
        {/* 왼쪽 우선순위 컬러 스트립 */}
        <div
          className="initiative-card__priority-strip"
          style={{ background: priorityColor }}
        />

        <div
          className="initiative-card__content"
          style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
        >
          {/* 상단: Initiative 라벨 + 카테고리 태그 + 화살표 */}
          <div className="initiative-card__header">
            <div className="initiative-card__header-left">
              <span className="initiative-card__label">Initiative</span>
              <span
                className="initiative-card__tag"
                style={{ color: tagColor }}
              >
                {category}
              </span>
            </div>
            <RightOutlined className="initiative-card__arrow" />
          </div>

          {/* 제목 */}
          <div className="initiative-card__title">{initiative.title}</div>
        </div>
      </Card>
    </div>
  )
}

/**
 * Initiative 칸반 컬럼 컴포넌트
 * @param {Object} props
 * @param {string} props.title - 컬럼 제목 (카테고리명)
 * @param {Array} props.tasks - 태스크 목록
 * @param {Function} props.onTaskClick - 태스크 클릭 핸들러
 */
const InitiativeColumn = ({ title, tasks, onTaskClick }) => {
  // tasks가 배열인지 확인 (방어 코드)
  const taskList = Array.isArray(tasks) ? tasks : []
  const isEmpty = taskList.length === 0

  return (
    <div className="initiative-column">
      <div className="initiative-column__title">{title}</div>
      <div className="initiative-column__content">
        {isEmpty ? (
          <div className="initiative-column__empty">업무가 비어있어요</div>
        ) : (
          taskList.map((item) => (
            <InitiativeCard
              key={item.id}
              initiative={item}
              category={title}
              onClick={onTaskClick}
            />
          ))
        )}
      </div>
    </div>
  )
}

/**
 * Initiative 칸반보드 컴포넌트
 * @param {Object} props
 * @param {string} props.objectiveNumber - Objective 번호 (예: "01")
 * @param {string} props.objectiveTitle - Objective 제목
 * @param {Object} props.tasksByCategory - 카테고리별 태스크 객체
 * @param {Function} props.onTaskClick - 태스크 클릭 핸들러
 * @param {Function} props.onCreateClick - 생성 버튼 클릭 핸들러
 * @param {boolean} props.showCreateButton - 생성 버튼 표시 여부
 */
const InitiativeKanbanBoard = ({
  objective,
  objectiveNumber = '01',
  objectiveTitle = '',
  tasksByCategory = {},
  onTaskClick,
  onCreateClick,
  onObjectiveClick,
  showCreateButton = true,
}) => {
  const categories = ['개선', '신규', '정책/기준', '사고 대응', '기타']

  return (
    <div className="initiative-kanban-board">
      {/* 2. 보드 헤더 (Objective 제목) */}
      <div className="initiative-kanban-board__header">
        <div
          className="initiative-kanban-board__header-left"
          onClick={() => onObjectiveClick?.(objective)}
          style={{ cursor: 'pointer' }}
        >
          <Text className="initiative-kanban-board__objective-label">
            {objectiveNumber}. Objective
          </Text>
          <div>
            <Text className="initiative-kanban-board__objective-title">
              {objectiveTitle}
            </Text>
            <Text
              style={{
                fontSize: '16px',
                color: '#585F65',
                fontWeight: '600',
                letterSpacing: '-0.4px',
                marginLeft: '16px',
              }}
            >
              상세 보기
              <RightOutlined
                style={{ width: '16px', height: '16px', color: '#585F65' }}
              />
            </Text>
          </div>
        </div>
        {showCreateButton && (
          <Button
            className="initiative-kanban-board__create-btn"
            icon={<PlusOutlined />}
            iconPosition="end"
            onClick={onCreateClick}
          >
            Initiative 업무 생성
          </Button>
        )}
      </div>

      {/* 3. 칸반 컬럼 영역 */}
      <div className="initiative-kanban-board__columns">
        {categories.map((category) => (
          <InitiativeColumn
            key={category}
            title={category}
            tasks={tasksByCategory[category]} // 데이터 바인딩 수정
            onTaskClick={onTaskClick}
          />
        ))}
      </div>
    </div>
  )
}
export default InitiativeKanbanBoard
